import { useEffect, useMemo, useState } from 'react';
import { sheetUrl, byColumnUrl } from './config';
import { useTheme } from './theme';

// Safe-ish non-cryptographic PIN hash (mitigates casual sheet-reading).
// NOTE: the sheet is still public; for serious security, move writes behind a
// server (Vercel Function) that stores only hashes.
async function hashPin(pin) {
  try {
    const enc = new TextEncoder().encode(String(pin));
    const buf = await crypto.subtle.digest('SHA-256', enc);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return '';
  }
}

function nowIso() {
  return new Date().toISOString();
}

const isYes = (v) => {
  if (v === undefined || v === null) return false;
  const s = String(v).trim().toLowerCase();
  if (s === '') return false;
  return !['no', 'false', '0', 'n'].includes(s);
};

// The Members sheet is keyed by memberNumber, NOT by an `id` column — it has
// no id column, and the rest of the app already looks members up this way
// (see enrichRoleFromMembers in App.js). Updates therefore PATCH by column.
// Keep memberNumber unique on the sheet: a PATCH by column would otherwise
// write to every matching row. validate() enforces that for new members, and
// the Member # field is disabled while editing so the key cannot drift.
const memberNumberOf = (r) =>
  String(r?.memberNumber ?? r?.['Member Number'] ?? '').trim();

const stylesFor = (theme, isMobile = false) => {
  const dark = theme === 'dark';
  return {
    page: {
      padding: isMobile ? 10 : 16,
      fontFamily: 'Arial',
      background: dark ? '#0f172a' : '#f8fafc',
      color: dark ? '#e5e7eb' : '#0f172a',
      minHeight: '100vh',
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      gap: 12,
      flexWrap: 'wrap',
      marginBottom: 10,
      maxWidth: 1100,
    },
    kicker: {
      fontSize: 12,
      color: dark ? '#94a3b8' : '#64748b',
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    h2: { margin: 0, fontSize: 24, color: dark ? '#f8fafc' : '#0f172a' },
    headerActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
    btn: {
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#111827' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      cursor: 'pointer',
      fontSize: 14,
    },
    btnPrimary: {
      padding: '10px 14px',
      borderRadius: 12,
      border: `1px solid ${dark ? '#0ea5e9' : '#0284c7'}`,
      background: '#0284c7',
      color: '#fff',
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: 800,
    },
    btnDanger: {
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${dark ? '#7f1d1d' : '#ef4444'}`,
      background: '#991b1b',
      color: 'white',
      cursor: 'pointer',
      fontSize: 14,
    },
    card: {
      border: `1px solid ${dark ? '#1f2937' : '#e5e7eb'}`,
      borderRadius: 14,
      padding: 14,
      marginBottom: 12,
      background: dark ? 'rgba(17, 24, 39, 0.7)' : '#ffffff',
      boxShadow: dark
        ? '0 10px 24px rgba(0,0,0,0.25)'
        : '0 10px 24px rgba(15, 23, 42, 0.08)',
      maxWidth: 1100,
    },
    label: {
      fontSize: 12,
      color: dark ? '#cbd5e1' : '#475569',
      fontWeight: 700,
      marginBottom: 6,
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 12,
      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
      background: dark ? '#0b1220' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontSize: 14,
      boxSizing: 'border-box',
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: 10,
    },
    // Card layout used on narrow viewports in place of the wide table.
    memberCard: {
      border: `1px solid ${dark ? '#1f2937' : '#e5e7eb'}`,
      borderRadius: 12,
      padding: 12,
      marginBottom: 10,
      background: dark ? 'rgba(2,6,23,0.35)' : '#fcfdff',
    },
    memberCardTopRow: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: 8,
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    },
    memberCardName: {
      fontWeight: 800,
      fontSize: 15,
      color: dark ? '#f8fafc' : '#0f172a',
      wordBreak: 'break-word',
    },
    memberCardNum: {
      fontSize: 12,
      color: dark ? '#94a3b8' : '#64748b',
      marginTop: 2,
    },
    memberCardFieldRow: {
      display: 'flex',
      gap: 6,
      marginTop: 6,
      fontSize: 13,
      flexWrap: 'wrap',
    },
    memberCardFieldLabel: {
      color: dark ? '#94a3b8' : '#64748b',
      minWidth: 56,
      fontWeight: 700,
    },
    memberCardFieldValue: {
      color: dark ? '#e5e7eb' : '#0f172a',
      wordBreak: 'break-word',
      flex: 1,
    },
    memberCardActions: {
      display: 'flex',
      gap: 8,
      marginTop: 10,
      flexWrap: 'wrap',
    },
    muted: { color: dark ? '#94a3b8' : '#64748b', fontSize: 12 },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: 900, fontSize: 13 },
    th: {
      textAlign: 'left',
      padding: 8,
      borderBottom: `1px solid ${dark ? '#1f2937' : '#e5e7eb'}`,
      whiteSpace: 'nowrap',
    },
    td: {
      padding: 8,
      borderBottom: `1px solid ${dark ? '#111827' : '#f1f5f9'}`,
      verticalAlign: 'top',
    },
    pill: {
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 800,
    },
    pillOk: { background: 'rgba(34,197,94,0.15)', color: '#16a34a' },
    pillOff: { background: 'rgba(148,163,184,0.15)', color: '#64748b' },
  };
};

const ROLES = ['member', 'board', 'chef', 'kitchen', 'accountant', 'admin'];
const BLANK_FORM = {
  id: '',
  memberNumber: '',
  name: '',
  email: '',
  allergy: '',
  role: 'member',
  active: 'yes',
  pin: '',
};

export default function AdminMembers({ authenticatedUser }) {
  const [theme, setTheme] = useTheme();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 720 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const S = useMemo(() => stylesFor(theme, isMobile), [theme, isMobile]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [form, setForm] = useState(BLANK_FORM);
  // memberNumber of the row being edited; null when adding a new member.
  const [editing, setEditing] = useState(null);

  const createdBy = authenticatedUser?.memberNumber || authenticatedUser?.name || 'admin';

  // SheetDB answers a PATCH that matched NO rows with HTTP 200 and
  // {"updated":0}, so res.ok alone is not proof of a write — a bad key would
  // report "Saved." having changed nothing. Treat a zero-row update as an
  // error instead.
  const patchMember = async (num, data) => {
    const res = await fetch(byColumnUrl('Members', 'memberNumber', num), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data }),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(text || `PATCH failed (${res.status})`);
    let updated;
    try {
      updated = JSON.parse(text)?.updated;
    } catch {
      updated = undefined; // non-JSON body; fall through to treating it as OK
    }
    if (updated === 0) throw new Error(`No member row matched #${num}.`);
  };

  const fetchMembers = async () => {
    setLoading(true);
    setStatus('');
    try {
      const res = await fetch(sheetUrl('Members'), { cache: 'no-store' });
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('AdminMembers: fetch failed', e);
      setRows([]);
      setStatus('Failed to load members.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows
      .filter((r) => (showInactive ? true : isYes(r.active)))
      .filter((r) => {
        if (!needle) return true;
        const blob = [
          r.memberNumber,
          r['Member Number'],
          r.name,
          r.email,
          r.role,
          r.allergy,
        ]
          .map((x) => (x == null ? '' : String(x).toLowerCase()))
          .join(' ');
        return blob.includes(needle);
      });
  }, [rows, q, showInactive]);

  const resetForm = () => {
    setEditing(null);
    setForm(BLANK_FORM);
  };

  const beginEdit = (r) => {
    const num = memberNumberOf(r);
    if (!num) {
      alert('This row has no member number, so it cannot be edited here. Add one on the Members sheet first.');
      return;
    }
    setEditing(num);
    setForm({
      id: r.id || '',
      memberNumber: num,
      name: r.name || '',
      email: r.email || '',
      allergy: r.allergy || '',
      role: r.role || 'member',
      active: isYes(r.active) ? 'yes' : 'no',
      pin: '', // never preload PIN
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validate = () => {
    if (!form.memberNumber.trim()) return 'Member number is required.';
    if (!form.name.trim()) return 'Name is required.';
    if (!editing && !form.pin.trim()) return 'PIN is required for new members.';
    if (form.pin && form.pin.trim().length < 4)
      return 'PIN must be at least 4 characters.';
    if (!editing) {
      const existing = rows.find(
        (r) => memberNumberOf(r) === form.memberNumber.trim()
      );
      if (existing) return `Member number ${form.memberNumber} already exists.`;
    }
    return '';
  };

  const submit = async () => {
    const err = validate();
    if (err) { alert(err); return; }
    setStatus('Saving…');

    const payload = {
      memberNumber: form.memberNumber.trim(),
      name: form.name.trim(),
      email: form.email.trim(),
      allergy: form.allergy.trim(),
      role: form.role,
      active: form.active === 'yes' ? 'yes' : 'no',
      updatedAt: nowIso(),
      updatedBy: String(createdBy),
    };

    // Only set PIN fields when we have one (add or password change)
    if (form.pin && form.pin.trim()) {
      payload.pin = form.pin.trim(); // kept for current login compatibility
      payload.pinHash = await hashPin(form.pin.trim());
    }

    try {
      if (editing) {
        await patchMember(editing, payload);
      } else {
        payload.createdAt = nowIso();
        payload.createdBy = String(createdBy);
        const res = await fetch(sheetUrl('Members'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: [payload] }),
        });
        if (!res.ok) throw new Error(await res.text());
      }
      setStatus('Saved.');
      resetForm();
      await fetchMembers();
    } catch (e) {
      console.error('AdminMembers: save failed', e);
      setStatus('Save failed.');
      alert('Failed to save member.');
    }
  };

  const deactivate = async (r) => {
    const num = memberNumberOf(r);
    if (!num) return alert('This row has no member number, so it cannot be updated here.');
    if (!window.confirm(`Deactivate ${r.name} (#${num})? They will no longer be able to log in.`))
      return;
    try {
      await patchMember(num, {
        active: 'no',
        updatedAt: nowIso(),
        updatedBy: String(createdBy),
      });
      await fetchMembers();
    } catch (e) {
      console.error('AdminMembers: deactivate failed', e);
      alert('Failed to deactivate member.');
    }
  };

  const reactivate = async (r) => {
    const num = memberNumberOf(r);
    if (!num) return alert('This row has no member number, so it cannot be updated here.');
    try {
      await patchMember(num, {
        active: 'yes',
        updatedAt: nowIso(),
        updatedBy: String(createdBy),
      });
      await fetchMembers();
    } catch (e) {
      console.error('AdminMembers: reactivate failed', e);
      alert('Failed to reactivate member.');
    }
  };

  return (
    <div style={S.page}>
      <div style={S.headerRow}>
        <div>
          <div style={S.kicker}>Admin</div>
          <h2 style={S.h2}>👥 Members</h2>
          <div style={S.muted}>
            Add, edit, or deactivate members. Writes to the <b>Members</b> sheet.
          </div>
        </div>

        <div style={S.headerActions}>
          <button style={S.btn} onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}>
            {theme === 'dark' ? '🌙 Dark' : '☀️ Light'}
          </button>
          <button style={S.btn} onClick={fetchMembers}>Refresh</button>
        </div>
      </div>

      {/* Composer */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
          <div style={{ fontWeight: 800 }}>
            {editing ? `Editing member #${editing}` : 'Add new member'}
          </div>
          {editing ? (
            <button style={S.btn} onClick={resetForm}>Cancel edit</button>
          ) : null}
        </div>

        <div style={S.formGrid}>
          <div>
            <div style={S.label}>Member #</div>
            <input
              style={S.input}
              value={form.memberNumber}
              onChange={(e) => setForm((f) => ({ ...f, memberNumber: e.target.value }))}
              placeholder="e.g. 201"
              disabled={!!editing}
            />
          </div>
          <div>
            <div style={S.label}>Full Name</div>
            <input
              style={S.input}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="First & Last"
            />
          </div>
          <div>
            <div style={S.label}>Email</div>
            <input
              style={S.input}
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="name@example.com"
            />
          </div>
          <div>
            <div style={S.label}>Allergy / Notes</div>
            <input
              style={S.input}
              value={form.allergy}
              onChange={(e) => setForm((f) => ({ ...f, allergy: e.target.value }))}
              placeholder="e.g. peanuts, shellfish"
            />
          </div>
          <div>
            <div style={S.label}>Role</div>
            <select
              style={S.input}
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={S.label}>Active</div>
            <select
              style={S.input}
              value={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.value }))}
            >
              <option value="yes">yes</option>
              <option value="no">no</option>
            </select>
          </div>
          <div>
            <div style={S.label}>
              PIN {editing ? '(leave blank to keep current)' : '(required)'}
            </div>
            <input
              style={S.input}
              type="password"
              value={form.pin}
              onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value }))}
              placeholder="Login PIN"
              autoComplete="new-password"
            />
            <div style={S.muted}>
              Stored as both <code>pin</code> (for current login) and <code>pinHash</code> (SHA-256).
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button style={S.btnPrimary} onClick={submit}>
            {editing ? 'Save Changes' : 'Add Member'}
          </button>
          <div style={{ ...S.muted, alignSelf: 'center' }}>
            {status}
          </div>
        </div>
      </div>

      {/* List */}
      <div style={S.card}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <input
            style={{ ...S.input, maxWidth: 320 }}
            placeholder="Search name, member #, email, role…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          <div style={{ ...S.muted, marginLeft: 'auto' }}>
            {loading ? 'Loading…' : `${filtered.length} shown / ${rows.length} total`}
          </div>
        </div>

        {isMobile ? (
          // Mobile: stacked cards — no horizontal scroll, Edit always visible
          <div>
            {filtered.length === 0 ? (
              <div style={{ ...S.muted, padding: 12, textAlign: 'center' }}>
                {loading ? 'Loading…' : 'No members match.'}
              </div>
            ) : (
              filtered.map((r) => {
                const active = isYes(r.active);
                const num = r.memberNumber || r['Member Number'] || '—';
                return (
                  <div key={r.id || `${num}-${r.name}`} style={S.memberCard}>
                    <div style={S.memberCardTopRow}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={S.memberCardName}>{r.name || '—'}</div>
                        <div style={S.memberCardNum}>#{num} · {r.role || 'member'}</div>
                      </div>
                      <span style={{ ...S.pill, ...(active ? S.pillOk : S.pillOff) }}>
                        {active ? 'active' : 'inactive'}
                      </span>
                    </div>

                    {r.email && (
                      <div style={S.memberCardFieldRow}>
                        <span style={S.memberCardFieldLabel}>Email</span>
                        <span style={S.memberCardFieldValue}>{r.email}</span>
                      </div>
                    )}
                    {r.allergy && (
                      <div style={S.memberCardFieldRow}>
                        <span style={S.memberCardFieldLabel}>Allergy</span>
                        <span style={S.memberCardFieldValue}>{r.allergy}</span>
                      </div>
                    )}

                    <div style={S.memberCardActions}>
                      <button style={S.btn} onClick={() => beginEdit(r)}>Edit</button>
                      {active ? (
                        <button style={S.btnDanger} onClick={() => deactivate(r)}>
                          Deactivate
                        </button>
                      ) : (
                        <button style={S.btn} onClick={() => reactivate(r)}>
                          Reactivate
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>#</th>
                  <th style={S.th}>Name</th>
                  <th style={S.th}>Email</th>
                  <th style={S.th}>Role</th>
                  <th style={S.th}>Allergy</th>
                  <th style={S.th}>Active</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ ...S.td, textAlign: 'center', opacity: 0.6 }}>
                      {loading ? 'Loading…' : 'No members match.'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => {
                    const active = isYes(r.active);
                    return (
                      <tr key={r.id || `${r.memberNumber}-${r.name}`}>
                        <td style={S.td}>{r.memberNumber || r['Member Number'] || '—'}</td>
                        <td style={S.td}>{r.name || '—'}</td>
                        <td style={S.td}>{r.email || '—'}</td>
                        <td style={S.td}>{r.role || 'member'}</td>
                        <td style={S.td}>{r.allergy || '—'}</td>
                        <td style={S.td}>
                          <span style={{ ...S.pill, ...(active ? S.pillOk : S.pillOff) }}>
                            {active ? 'active' : 'inactive'}
                          </span>
                        </td>
                        <td style={S.td}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button style={S.btn} onClick={() => beginEdit(r)}>Edit</button>
                            {active ? (
                              <button style={S.btnDanger} onClick={() => deactivate(r)}>
                                Deactivate
                              </button>
                            ) : (
                              <button style={S.btn} onClick={() => reactivate(r)}>
                                Reactivate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ ...S.muted, marginTop: 8 }}>
          Required columns on the <b>Members</b> sheet:
          {' '}<code>memberNumber</code>, <code>name</code>, <code>email</code>,
          {' '}<code>allergy</code>, <code>role</code>, <code>active</code>,
          {' '}<code>pin</code>, <code>pinHash</code>, <code>createdAt</code>,
          {' '}<code>createdBy</code>, <code>updatedAt</code>, <code>updatedBy</code>.
          {' '}Rows are matched by <code>memberNumber</code>, so keep it unique.
        </div>
      </div>
    </div>
  );
}
